import WebSocket from "ws";
import type { Process } from "@puppeteer/browsers";

const BROWSER_CLOSE_TIMEOUT = 5_000;

type BrowserProcess = Pick<Process, "hasClosed" | "kill">;

/**
 * Chrome exited before announcing its DevTools endpoint — i.e. it never got
 * as far as running.
 *
 * Distinguished from every other launch failure because it is the only one
 * that can implicate the installation itself. Once Chrome has printed its
 * banner it has already loaded the resources a partial download would be
 * missing, so a failure after that point (a readiness probe timing out, a
 * profile directory that cannot be created) says nothing about the install
 * and must not be "recovered" by deleting it.
 *
 * The underlying message is preserved so callers and logs are unaffected.
 */
export class BrowserStartupError extends Error {
	override readonly name = "BrowserStartupError";

	constructor(cause: unknown) {
		super(cause instanceof Error ? cause.message : String(cause), { cause });
	}
}

function waitForGracefulClose(
	browserProcess: BrowserProcess,
	wsEndpoint: string,
	timeoutMs: number
): Promise<boolean> {
	return new Promise((resolve) => {
		const socket = new WebSocket(wsEndpoint);
		const timeout = setTimeout(() => finish(false), timeoutMs);
		let finished = false;

		function finish(closed: boolean) {
			if (finished) return;
			finished = true;
			clearTimeout(timeout);
			socket.terminate();
			resolve(closed);
		}

		void browserProcess.hasClosed().then(
			() => finish(true),
			() => finish(false)
		);
		socket.once("open", () => {
			socket.send(
				JSON.stringify({ id: 1, method: "Browser.close" }),
				(error) => {
					if (error) finish(false);
				}
			);
		});
		socket.once("error", () => finish(false));
	});
}

export async function waitForExit(
	browserProcess: BrowserProcess,
	timeoutMs = BROWSER_CLOSE_TIMEOUT
): Promise<void> {
	let timeout: NodeJS.Timeout | undefined;
	await Promise.race([
		browserProcess.hasClosed().catch(() => {}),
		new Promise<void>((resolve) => {
			timeout = setTimeout(resolve, timeoutMs);
		}),
	]);
	clearTimeout(timeout);
}

export async function closeBrowserProcess(
	browserProcess: BrowserProcess,
	wsEndpoint: string,
	timeoutMs = BROWSER_CLOSE_TIMEOUT
): Promise<void> {
	if (await waitForGracefulClose(browserProcess, wsEndpoint, timeoutMs)) return;

	// Process.kill() terminates the whole process tree with taskkill on Windows
	// and SIGKILL on the detached process group on POSIX systems.
	browserProcess.kill();
	await waitForExit(browserProcess, timeoutMs);
}
